import { useCallback, useMemo } from 'react';
import { Grid, Stack, Box } from '@mui/material';
import { useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';

import {
  Button,
  TextField,
  Typography,
  KeyboardDateTimePicker,
  IconButton,
  DeleteForeverIcon as DeleteIcon,
  InfoBox,
} from '@occc-app/design-system';
import { getUserTimezoneFullInfo } from '@occc-app/lib/date';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';
import { formatUpperCase } from '@occc-app/lib/finalFormUtils';

import { MAX_PROMOCODES_LENGTH } from './promocodes-configuration.constants';
import { getPromocodeInitialValues } from './promocodes-configuration.utils';
import {
  isPromocodesGlobalFormValues,
  PromocodesConfigureFormValues,
} from './promocodes-configuration.types';

type PromocodeProps = {
  fieldName: string;
  index: number;
  deleteDisabled?: boolean;
  minEndDate: Date | null;
  onDeleteClick(): void;
};

const Promocode = (props: PromocodeProps) => {
  const { fieldName, index, deleteDisabled, minEndDate, onDeleteClick } = props;

  return (
    <Stack spacing={1}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography fontWeight='bold'>{`Promo Code ${index + 1}`}</Typography>
        <IconButton onClick={onDeleteClick} disabled={deleteDisabled}>
          <DeleteIcon color={deleteDisabled ? 'disabled' : 'error'} />
        </IconButton>
      </Stack>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              name={`${fieldName}.code`}
              label={`Promo Code ${index + 1}`}
              fieldProps={{ parse: (value: unknown) => formatUpperCase(value).trim() }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField name={`${fieldName}.description`} label='Description' />
          </Grid>
          <Grid item xs={6}>
            <KeyboardDateTimePicker
              label='Start Date'
              name={`${fieldName}.startDate`}
              disablePast
            />
          </Grid>
          <Grid item xs={6}>
            <KeyboardDateTimePicker
              label='End Date'
              name={`${fieldName}.endDate`}
              disablePast
              minDateTime={minEndDate || undefined}
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

type PromocodesGlobalConfigurationProps = {
  values: PromocodesConfigureFormValues;
};

export const PromocodesGlobalConfiguration = (props: PromocodesGlobalConfigurationProps) => {
  const promocodesValues = props.values.promocodes;
  const isGlobal = promocodesValues !== undefined && isPromocodesGlobalFormValues(promocodesValues);

  const form = useForm();
  const { fields } = useFieldArray('promocodes.promocodes', { subscription: { length: true } });
  const timezone = useMemo(() => getUserTimezoneFullInfo(), []);
  const { isEditable } = useCanEditFormFieldsContext();

  const handleRemovePromocode = useCallback(
    (index: number) => {
      const isLast = fields.length === 1;
      fields.remove(index);
      if (isLast) {
        form.change('promocodes.promocodes', []);
      }
    },
    [fields, form],
  );

  const handleAddPromocode = useCallback(() => {
    fields.push(getPromocodeInitialValues());
  }, [fields]);

  if (!isGlobal) {
    return null;
  }

  const promocodes = promocodesValues.promocodes ?? [];

  return (
    <>
      <Stack spacing={2}>
        {fields.map((fieldName, index) => (
          <Promocode
            key={fieldName}
            fieldName={fieldName}
            index={index}
            deleteDisabled={
              !isEditable ||
              (promocodes[index]?.isSaved && (promocodes[index]?.redemptions ?? 0) > 0)
            }
            onDeleteClick={() => handleRemovePromocode(index)}
            minEndDate={promocodes[index]?.startDate ?? null}
          />
        ))}
      </Stack>
      {(fields?.length || 0) >= MAX_PROMOCODES_LENGTH && (
        <Typography gutterBottom fontWeight='bold' color='error' align='center'>
          You may only add {MAX_PROMOCODES_LENGTH} Promo Codes per Offer
        </Typography>
      )}
      <InfoBox type='inter'>
        <Typography variant='body2'>{`Time zone is set to ${timezone}`}</Typography>
      </InfoBox>
      {isEditable && (fields?.length || 0) < MAX_PROMOCODES_LENGTH && (
        <Button onClick={handleAddPromocode} variant='contained' sx={{ alignSelf: 'center' }}>
          {fields.length === 0 ? '+ Add promo code' : '+ Add another promo code'}
        </Button>
      )}
    </>
  );
};
